import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Heart, ShoppingBag, User, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const links = [
  { label: "Men", to: "/shop?audience=men" },
  { label: "Boys", to: "/shop?audience=boys" },
  { label: "New Arrivals", to: "/shop?new_arrival=true" },
  { label: "Festive Edit", to: "/shop?occasion=Festive" },
  { label: "Lookbook", to: "/lookbook" },
  { label: "About", to: "/about" },
];

export function Navbar() {
  const { count } = useCart();
  const { count: wishCount } = useWishlist();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/shop?search=${encodeURIComponent(q.trim())}`);
    setOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-[#FBF7DE]/90 backdrop-blur-md border-b border-[var(--line)]">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
        <div className="flex items-center justify-between h-20">
          <button className="lg:hidden text-[var(--bronze)]" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          <Logo className="h-11 lg:h-12" />

          <nav className="hidden lg:flex items-center gap-9">
            {links.map((l) => (
              <Link key={l.label} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
                className="uplabel link-underline text-[var(--bronze)] hover:text-[var(--olive)] transition-colors">
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4 lg:gap-5">
            <form onSubmit={onSearch} className="hidden md:flex items-center border-b border-[var(--line)] pb-1">
              <input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search" data-testid="search-input"
                className="bg-transparent text-sm w-28 focus:w-40 transition-all outline-none placeholder:text-[var(--olive)]/60 text-[var(--bronze)]"
              />
              <button type="submit" aria-label="Search"><Search className="w-4 h-4 text-[var(--olive)]" /></button>
            </form>

            <DropdownMenu>
              <DropdownMenuTrigger data-testid="account-menu-btn" className="text-[var(--bronze)] hover:text-[var(--olive)] transition-colors outline-none">
                <User className="w-5 h-5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#FBF7DE] border-[var(--line)]">
                {user ? (
                  <>
                    <DropdownMenuItem asChild><Link to="/account" data-testid="menu-account">My Account</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/account">My Orders</Link></DropdownMenuItem>
                    {user.role === "admin" && <DropdownMenuItem asChild><Link to="/admin" data-testid="menu-admin">Admin Dashboard</Link></DropdownMenuItem>}
                    <DropdownMenuItem onClick={logout} data-testid="menu-logout">Logout</DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuItem asChild><Link to="/login" data-testid="menu-login">Sign In</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/register">Create Account</Link></DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/wishlist" data-testid="nav-wishlist" className="relative text-[var(--bronze)] hover:text-[var(--olive)] transition-colors">
              <Heart className="w-5 h-5" />
              {wishCount > 0 && <span className="absolute -top-2 -right-2 bg-[var(--maroon)] text-[#FBF7DE] text-[0.6rem] w-4 h-4 grid place-items-center rounded-full">{wishCount}</span>}
            </Link>

            <Link to="/cart" data-testid="nav-cart" className="relative text-[var(--bronze)] hover:text-[var(--olive)] transition-colors">
              <ShoppingBag className="w-5 h-5" />
              {count > 0 && <span className="absolute -top-2 -right-2 bg-[var(--olive)] text-[#FBF7DE] text-[0.6rem] w-4 h-4 grid place-items-center rounded-full">{count}</span>}
            </Link>
          </div>
        </div>
      </div>

      {open && (
        <nav className="lg:hidden border-t border-[var(--line)] px-5 py-4 flex flex-col gap-4 bg-[#FBF7DE]">
          <form onSubmit={onSearch} className="flex items-center border-b border-[var(--line)] pb-2 mb-1">
            <Search className="w-4 h-4 text-[var(--olive)] mr-2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search kurtas" className="bg-transparent text-sm flex-1 outline-none text-[var(--bronze)]" />
          </form>
          {links.map((l) => (
            <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="uplabel text-[var(--bronze)]">{l.label}</Link>
          ))}
        </nav>
      )}
    </header>
  );
}
